import type { Coding, Task } from 'fhir/r4';
import moment from 'moment';

export const formatDateForFHIR = (date: Date | string): string => {
  return moment(date).utc().format('YYYY-MM-DD');
};

export const getOrganizationReference = (orgCode: string): { code: string; display: string } => {
  return { code: orgCode.toUpperCase(), display: orgCode.toLowerCase() };
};

/**
 * The patient id embedded in `Task.for.reference` (`"Patient/{id}"`).
 *
 * SUSPECTED DEFECT — the return is asserted `string`, but all three of
 * `Task.for` (`0..1`), `Reference.reference` (`0..1`) and the `[1]` index are
 * genuinely absent-able, so this is `string | undefined` in truth. Every
 * consumer in `useTaskAssignment` declares it required:
 *
 *   - `ProvenanceBuilder.build(..., patientId, ...)` interpolates it into
 *     `` `Patient/${patientId}` ``, so an absent id does NOT throw — it writes
 *     the literal reference `"Patient/undefined"` into a Provenance record on
 *     the FHIR server. That is silent data corruption rather than a crash,
 *     which is why it has gone unnoticed.
 *   - `FlagBuilder.build(patientId, ...)` has the same shape.
 *   - The `updateTasksAndPatientStatus` hook payload sends it as `patientId`.
 *
 * Reported, not fixed. Guarding here would skip provenance/flag creation for
 * such a task; throwing would break the save. Both are behaviour changes, and
 * which is right depends on whether a Task without a subject is reachable at
 * all in this deployment — the FHIR server may well enforce `Task.for`, in
 * which case the honest repair is a validated read at the boundary rather than
 * a guard here. Extracted so the assertion is stated once instead of at five
 * call sites.
 */
export const getTaskPatientId = (task: Task): string => {
  // Written as a widened local rather than `...?.split('/')[1]!` so the
  // assertion is not spelled on an optional chain (which would be claiming the
  // chain cannot short-circuit — it can, and that is exactly the defect above).
  const patientId: string | undefined = task.for?.reference?.split('/')[1];
  return patientId as string;
};

export const isAutomatedTask = (task: Task): boolean => {
  return task?.code?.coding?.[0]?.code === 'automated';
};

export const isFoodDeliveryTask = (task: Task): boolean => {
  return task?.code?.coding?.[0]?.code === 'food-delivery';
};

export const isConsultationTask = (task: Task): boolean => {
  const coding = task?.code?.coding;
  if (!coding?.length) return false;
  const taskCode =
    coding.find((entry) => entry?.system === 'https://projectwell.io/fhir/codes/task-codes')?.code ??
    coding[0]?.code;
  return taskCode === 'consultation';
};

const TASK_SUB_TYPE_SYSTEM = 'https://projectwell.io/fhir/identifiers/task-sub-type';

/**
 * True when a consult is FFS-MNT (consult type `ffs-mnt-1:1` or reason/display containing ffs-mnt).
 * Used to gate cancel/fail member-event due-date capture for FFS consults only.
 */
export const isFfsMntConsultationTask = (task: Task): boolean => {
  if (!isConsultationTask(task)) return false;

  const consultType = task.identifier?.find((id) => id.system === TASK_SUB_TYPE_SYSTEM)?.value ?? '';
  if (consultType.toLowerCase().includes('ffs-mnt')) {
    return true;
  }

  const reasonText = task.reasonCode?.text ?? '';
  const reasonDisplay = task.reasonCode?.coding?.map((c) => c.display ?? c.code ?? '').join(' ') ?? '';
  const description = task.description ?? '';
  return /ffs[- ]?mnt/i.test(`${reasonText} ${reasonDisplay} ${description}`);
};

/** True when bulk status is in-progress and at least one selected consult task is not already in-progress. */
export const needsConsultInProgressConfirmBulk = (tasks: Task[], bulkStatus: string | null): boolean => {
  return (
    bulkStatus === 'in-progress' &&
    tasks.some((t) => isConsultationTask(t) && t.status !== 'in-progress')
  );
};

/** True when a consult task is being set to in-progress from another status. */
export const needsConsultInProgressConfirmSingle = (task: Task, newStatus: Task['status']): boolean => {
  return isConsultationTask(task) && newStatus === 'in-progress' && task.status !== 'in-progress';
};

/** Statuses that should remind staff to set the consult due date before saving. */
const CONSULT_DUE_DATE_CONFIRM_STATUSES: ReadonlyArray<Task['status']> = ['cancelled', 'failed'];

/** True when bulk status needs an FFS-MNT consult due-date reminder for at least one selected task. */
export const needsConsultCancelDueDateConfirmBulk = (tasks: Task[], bulkStatus: string | null): boolean => {
  return (
    CONSULT_DUE_DATE_CONFIRM_STATUSES.includes(bulkStatus as Task['status']) &&
    tasks.some((t) => isFfsMntConsultationTask(t) && t.status !== bulkStatus)
  );
};

/**
 * True when an FFS-MNT consult is being set to cancelled or failed from another status.
 * Reminds staff to set the due date to the member’s cancellation/failure date first.
 */
export const needsConsultCancelDueDateConfirmSingle = (task: Task, newStatus: Task['status']): boolean => {
  return (
    isFfsMntConsultationTask(task) &&
    CONSULT_DUE_DATE_CONFIRM_STATUSES.includes(newStatus) &&
    task.status !== newStatus
  );
};

/**
 * Sets consult `executionPeriod` to the member cancel/no-show date so due date and
 * failed/cancelled status can be written in the same FHIR update.
 *
 * @param task - Consultation task being marked cancelled or failed.
 * @param memberEventDate - Calendar date (`YYYY-MM-DD` or parseable) of the cancel/no-show.
 */
export const applyConsultMemberEventDueDate = (task: Task, memberEventDate: string): void => {
  const date = moment(memberEventDate).format('YYYY-MM-DD');
  task.executionPeriod = {
    start: date,
    end: date,
  };
};

/**
 * `Coding[] | undefined` is what the body accepts (`taskCode?.[0]?.code`) and
 * what the sole caller passes — `TaskHelper.getCode()` returns
 * `Coding[] | undefined`. An absent coding still yields `false`.
 */
export const isInterventionOrEnrollmentTask = (
  taskCode: Coding[] | undefined,
  reasonText: string
): boolean => {
  return (
    (taskCode?.[0]?.code === 'careplan' || taskCode?.[0]?.code === 'enrollment-action') &&
    (reasonText === 'intervention' || reasonText === 'enrollment')
  );
};

export const shouldUpdateExecutionPeriod = (
  dueDate: string,
  lastUpdated: string,
  startDate: string,
  endDate: string
): boolean => {
  return (
    moment(dueDate).format('YYYY-MM-DD') < moment(lastUpdated).format('YYYY-MM-DD') &&
    moment(dueDate).format('YYYY-MM-DD') < moment(startDate).format('YYYY-MM-DD') &&
    moment(dueDate).format('YYYY-MM-DD') < moment(endDate).format('YYYY-MM-DD')
  );
};
